import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, OnInit, TemplateRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import {
    MatLegacyAutocomplete as MatAutocomplete,
    MatLegacyAutocompleteTrigger as MatAutocompleteTrigger,
    MatLegacyAutocompleteModule
} from '@angular/material/legacy-autocomplete';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged } from 'rxjs/operators';
import { OptionModel } from 'src/app/shared/grid/filters/option.model';

function toOptionModel(items: any[]): OptionModel[] {
    return (
        items?.map((item) => {
            return {
                id: item?.id ?? 0,
                name: item?.name ?? '',
                email: item?.email ?? '',
                recruiterName: item?.recruiterName ?? ''
            };
        }) ?? []
    );
}

@UntilDestroy()
@Component({
    selector: 'autocomplete-multiselect',
    templateUrl: './autocomplete-multiselect.component.html',
    styleUrls: ['./autocomplete-multiselect.component.scss'],
    standalone: true,
    imports: [
        MatLegacyFormFieldModule,
        NgTemplateOutlet,
        MatIconModule,
        ReactiveFormsModule,
        MatLegacyChipsModule,
        NgClass,
        FormsModule,
        MatLegacyAutocompleteModule,
        MatLegacyCheckboxModule
    ]
})
export class AutocompleteMultiselectComponent implements OnInit {
    inputValueControl = new UntypedFormControl('');
    inputValue: string;
    listSelected: boolean;
    // List of items selected
    @Input({ transform: toOptionModel }) selectedItems: OptionModel[];
    @Input() maxDataRows: number = 50;
    @Input() isCheckbox: boolean = false;
    @Input() isChipClickEnabled = false;
    @Input({ transform: toOptionModel }) data: OptionModel[];
    @Input() placeholder: string = '';
    // Holds the user search text and will alert any subscribers when the text changes
    @Input() searchText: Subject<string>;
    @Input() minInputLength: number = 4;
    @Input() autocompleteClass: string;
    @Input() staticData = false;
    @Input() flavorText: TemplateRef<any>;
    @Input() flavorTextLineBreak = false;
    @Input() clearTextOnSelection = false;
    @Input() appearance = null;
    @Output() filterValueChanged = new EventEmitter<OptionModel[]>();
    @Output() chipClickEvent = new EventEmitter<OptionModel>();

    @ViewChild('auto') matAuto: MatAutocomplete;
    @ViewChild('searchTextInput') searchTextInput: ElementRef<HTMLInputElement>;
    @ViewChild('searchTextInput', { read: MatAutocompleteTrigger, static: false }) autoComplete: MatAutocompleteTrigger;

    ngOnInit() {
        // user enters in search text and then Select options are populated and displayed
        this.inputValueControl.valueChanges
            .pipe(
                // time in MS between key events
                debounceTime(250),
                distinctUntilChanged(),
                untilDestroyed(this)
            )
            .subscribe((inputValue) => {
                if ((inputValue && inputValue.length >= this.minInputLength) || inputValue === '') {
                    this.searchText.next(inputValue);
                }
            });

        this.filterValueChanged.pipe(delay(500), untilDestroyed(this)).subscribe(() => {
            if (this.autoComplete.panelOpen) {
                this.autoComplete.updatePosition();
            }
        });
    }

    clearAll() {
        this.selectedItems = [];
        this.clearTextInput();
        this.releaseFilter();
    }

    onRemove(item: OptionModel | any) {
        this.removeItem(item);
        this.clearTextInput();
        this.releaseFilter();
    }

    removeItem(item: OptionModel | any) {
        this.selectedItems = this.selectedItems.filter((value) => value.id !== item.id);
    }

    isSelected(item: OptionModel) {
        return this.selectedItems.some((cv) => cv.id === item.id);
    }

    optionSelected(item: OptionModel) {
        if (!this.isSelected(item)) {
            this.selectedItems.push(item);
        } else {
            this.removeItem(item);
        }
        if (this.clearTextOnSelection) {
            this.clearTextInput();
        }
        this.applyFilter();
    }

    applyFilter() {
        this.filterValueChanged.emit(this.selectedItems);
    }

    onChipClick(item: OptionModel | any): void {
        if (this.isChipClickEnabled) {
            this.chipClickEvent.emit(item);
        }
    }

    releaseFilter() {
        if (!this.staticData) {
            this.data = [];
        }
        if (!this.clearTextOnSelection) {
            this.clearTextInput();
        }
        this.applyFilter();
    }

    clearTextInput(): void {
        this.searchTextInput.nativeElement.value = '';
        this.inputValue = '';
    }
}
